import { freeCargoM3, freeOreHoldM3 } from "../../acceptedMissions";
import { getSystem, loadSystems } from "../../data/loadSystems";
import { MARKET_ITEMS, type MarketCategory } from "../../data/marketItems";
import { RESOURCE_TIER, volumePerUnitM3 } from "../../resourceCatalog";
import {
  baseStockQty,
  bestHaulProfit,
  canSellHere,
  currentMarketEpoch,
  msUntilNextEpoch,
  stationBuyPrice,
  stationSellPrice,
  stationStocks,
  type GalaxyRegion
} from "../../market/stationMarket";
import { CONTRABAND_DISCOUNT_MULTIPLIER, contrabandDiscountApplies } from "../../data/marketItems";
import { triggerSave } from "../persistence";
import { state } from "../state";
import type { ScreenContext } from "../app";
import { renderHeader } from "../components";
import { TRADE_STANDING_GAIN, TRADE_STANDING_MIN_VALUE_CR, adjustStanding, standingTier } from "../../factionStanding";
import { stationGuardFaction } from "../../stationGuards";
import { systemsWithinHops } from "../../galaxyGraph";
import { holdFill } from "../../market/holdMeter";
import { droneBuyAllowed, droneRowVisible, usableDroneNames } from "../../droneMarketVisibility";
import { isDroneItem } from "../../droneSystem";

/**
 * The Market: one screen to buy and sell (design doc 16.92, Don's mockup).
 *
 * Replaces the old two-tab Buy/Sell layout. Every tradeable thing is one row
 * carrying both sides of the transaction -- what this station charges, what it
 * pays, how many it has, how many you have -- because the decision a trader is
 * actually making is a comparison, and a tabbed layout hid half of it behind a
 * click.
 *
 * Rows are the UNION of what the station stocks and what is in the hold, not
 * just the catalogue: stations buy anything whether or not they sell it (Don's
 * ruling), so a captain must always be able to liquidate a hold full of
 * something this station has never heard of.
 */

type SortKey = "buy" | "sell" | "stationQty" | "cargoQty" | "name" | "type" | "haul" | "away" | "hops";

interface Row {
  name: string;
  category: MarketCategory | "Resource";
  /** Units the station has left this hour, after the captain's own purchases. */
  stationQty: number;
  stocked: boolean;
  buyPrice: number;
  sellPrice: number;
  cargoQty: number;
  volumePerUnit: number;
  /** Best profit per m3 hauling this to another station. null when unknown. */
  haulProfitPerM3: number | null;
  /** True when the figure means "buy here and haul"; false when it means
   *  "carry what you hold rather than selling it here". */
  haulIsBuyHere: boolean;
  haulTargetName: string | null;
  /** System id of the Highest Demand station, for the waypoint button (16.97). */
  haulTargetId: string | null;
  /**
   * What the best other station PAYS for this, in absolute credits per unit.
   *
   * 16.96: the screen used to show only the derived profit, so the two visible
   * prices were both local -- "Buy Price" next to "Sell Price" -- and read as
   * buy-here/sell-there. That pairing is a guaranteed loss by construction
   * (sell here is always buy here minus the spread), so a correct green profit
   * figure beside it looked like an arithmetic error, and the conclusion was
   * that the game had no trade economy. It has one; it was just never shown
   * the far half of the trade.
   */
  bestSellPrice: number | null;
  /**
   * Jumps from here to the station in `haulTargetName` (16.97).
   *
   * The gap 16.96 admitted to and Don asked to close: a margin means nothing
   * without the distance behind it, so a 24 CR/m3 run eleven jumps away was
   * outranking 18 CR/m3 next door with nothing on screen to say so. Displayed
   * rather than folded into the margin -- the captain knows what their fuel and
   * their patience are worth, and no coefficient here would.
   */
  hops: number | null;
  /** False for a drone no ship of the captain's can fit -- visible so it can
   *  still be liquidated, but not purchasable (16.97). */
  buyAllowed: boolean;
  sellable: boolean;
}

// UI position, not game state: which column is sorted and which way. Module
// scope so it survives a rerender without being persisted into a save.
let sortKey: SortKey = "haul";
let sortDesc = true;

// Typed quantities, per item and per side. Cleared when the screen unmounts by
// virtue of living here rather than in state -- a half-typed 40 should not
// follow the captain across the galaxy.
const buyQty = new Map<string, number>();
const sellQty = new Map<string, number>();

export function renderMarketScreen(container: HTMLElement, ctx: ScreenContext): void {
  const wrap = document.createElement("div");
  const system = getSystem(state.currentSystemId);
  const hasStation = !!system?.hasStation;
  const region = (system?.region ?? "neutral") as GalaxyRegion;
  const stationId = system?.id ?? "none";
  const stationClass = system?.stationClass ?? "hub";
  const epoch = currentMarketEpoch(Date.now());

  // Every other station, for the haul-profit column. Computed once per render
  // rather than per row -- 7 stations x 56 items is small, but it is still
  // pointless to redo it 56 times.
  const otherStations = loadSystems()
    .filter((s) => s.hasStation && s.id !== stationId)
    .map((s) => ({ id: s.id, region: s.region as GalaxyRegion, name: s.name }));

  // Hop counts to every system in one BFS (16.97), rather than a search per
  // row. The galaxy is one connected graph, so every station resolves.
  const hopsById = new Map<string, number>(
    systemsWithinHops(stationId, Infinity, loadSystems()).map((d) => [d.system.id, d.hops])
  );

  // Which drones this captain has any ship for: the flown hull plus the whole
  // fleet (16.97). Computed once -- it cannot change while the screen is open.
  const usableDrones = usableDroneNames([
    state.currentShip ? { role: state.currentShip.role, hullTier: state.currentShip.hullTier } : null,
    ...state.fleet.map((f) => ({ role: f.ship.role, hullTier: f.ship.hullTier }))
  ]);

  function purchaseKey(name: string): string {
    return `${epoch}|${stationId}|${name}`;
  }

  /**
   * Trade standing (16.95): doing real business somewhere counts for something.
   *
   * Worth 10% of a mission, awarded at most once per faction per market hour,
   * and only for a transaction over 5,000 CR. Two independent guards, because a
   * reputation that can be ground out one unit at a time is not a reputation --
   * and hauling and mining missions already pay full standing, so this never
   * needs to compete with the board.
   *
   * Nothing is awarded in the unowned core: there is no faction there to
   * impress.
   */
  function creditTradeStanding(valueCr: number): void {
    if (valueCr < TRADE_STANDING_MIN_VALUE_CR) return;
    const faction = stationGuardFaction(region);
    if (!faction) return;
    const key = `${epoch}|${faction}`;
    if (state.tradeStandingAwarded[key]) return;
    state.tradeStandingAwarded[key] = 1;
    state.standing = adjustStanding(state.standing, faction, TRADE_STANDING_GAIN);
  }

  function stationQtyFor(name: string): number {
    const base = baseStockQty(stationId, name, epoch, stationClass);
    const taken = state.stationPurchases[purchaseKey(name)] ?? 0;
    return Math.max(0, base - taken);
  }

  function isOre(name: string): boolean {
    return RESOURCE_TIER[name] !== undefined;
  }

  function freeSpaceFor(name: string): number {
    const ship = state.currentShip;
    if (!ship) return 0;
    return isOre(name)
      ? freeOreHoldM3(ship.oreHoldM3, state.cargo, state.acceptedMissions)
      : freeCargoM3(ship.cargoM3, state.cargo, state.acceptedMissions);
  }

  function unitsThatFit(name: string): number {
    const per = volumePerUnitM3(name) || 1;
    return Math.floor(freeSpaceFor(name) / per);
  }

  function effectiveBuyPrice(name: string): number {
    const base = stationBuyPrice(stationId, region, name, epoch);
    return contrabandDiscountApplies(name, standingTier(state.standing.Pirate) === "Friendly")
      ? base * CONTRABAND_DISCOUNT_MULTIPLIER
      : base;
  }

  function buildRows(): Row[] {
    const names = new Set<string>();
    if (hasStation) {
      MARKET_ITEMS.forEach((i) => {
        if (stationStocks(stationId, i.name, epoch)) names.add(i.name);
      });
    }
    // Anything in the hold is a row even where the station stocks none, so it
    // can always be sold.
    Object.keys(state.cargo).forEach((n) => {
      if (state.cargo[n] > 0) names.add(n);
    });

    return [...names]
      .filter((name) => droneRowVisible(name, usableDrones, state.cargo[name] ?? 0))
      .map((name) => {
      const item = MARKET_ITEMS.find((i) => i.name === name);
      const buyPrice = effectiveBuyPrice(name);
      const sellPrice = stationSellPrice(stationId, region, name, epoch);
      const per = volumePerUnitM3(name) || 1;
      const stocked = hasStation && stationStocks(stationId, name, epoch);
      const held = (state.cargo[name] ?? 0) > 0;
      // 16.95 fix: the haul figure is only meaningful against something the
      // captain can actually act on HERE.
      //
      // It used to be computed for every row, so a station's best "haul
      // opportunity" was routinely an item it does not sell -- profit measured
      // against a price nobody could pay. Sorted by haul, the top of the list
      // was phantoms and the real buys sat beneath them, which made a station
      // full of viable runs read as having none.
      //
      // Two genuinely different questions, and which one applies depends on
      // what the captain can do here:
      //   stocked  -> buy here, sell there:      sellThere - buyHere
      //   held     -> carry rather than dump:    sellThere - sellHere
      // Both are "CR/m3 gained by hauling instead of transacting here", so they
      // share the column honestly -- but the tooltip says which, because the
      // action they imply is different.
      //
      // Fixed-price equipment is excluded outright (16.97). A drone costs the
      // same at all 28 stations, so "the best place to sell it" is a category
      // error dressed up as advice -- the old screen named an arbitrary station
      // and printed a 0.00 margin beside it.
      const tradeable = !isDroneItem(name);
      const best =
        tradeable && (stocked || held) ? bestHaulProfit(name, stationId, region, otherStations, epoch) : null;
      const bestSellElsewhere = best ? best.profitPerUnit + stationBuyPrice(stationId, region, name, epoch) : null;
      const haulProfitPerUnit =
        best === null
          ? null
          : stocked
            ? best.profitPerUnit
            : (bestSellElsewhere as number) - sellPrice;
      const target = best ? otherStations.find((s) => s.id === best.bestStationId) : null;
      return {
        name,
        category: item?.category ?? "Resource",
        stationQty: hasStation ? stationQtyFor(name) : 0,
        stocked,
        buyPrice,
        sellPrice,
        cargoQty: state.cargo[name] ?? 0,
        volumePerUnit: per,
        haulProfitPerM3: haulProfitPerUnit === null ? null : haulProfitPerUnit / per,
        haulIsBuyHere: stocked,
        haulTargetName: target?.name ?? null,
        haulTargetId: target?.id ?? null,
        bestSellPrice:
          best === null || !target
            ? null
            : stationSellPrice(target.id, target.region as GalaxyRegion, name, epoch),
        hops: target ? (hopsById.get(target.id) ?? null) : null,
        buyAllowed: droneBuyAllowed(name, usableDrones),
        sellable: canSellHere(name, region)
      };
    });
  }

  function sortRows(rows: Row[]): Row[] {
    const dir = sortDesc ? -1 : 1;
    const value = (r: Row): number | string => {
      switch (sortKey) {
        case "buy":
          return r.buyPrice;
        case "sell":
          return r.sellPrice;
        case "stationQty":
          return r.stationQty;
        case "cargoQty":
          return r.cargoQty;
        case "type":
          return r.category;
        case "haul":
          // Unknown sorts last in either direction rather than reading as the
          // worst possible route -- "no data" and "a bad route" are different
          // answers and must not look alike.
          return r.haulProfitPerM3 ?? -Infinity;
        case "away":
          return r.bestSellPrice ?? -Infinity;
        case "hops":
          // Nearest-first is what a captain wants, and "no route to quote"
          // must not masquerade as the far end of the galaxy -- so unknown
          // sorts to the same end in both directions, like haul above.
          return r.hops ?? (sortDesc ? -Infinity : Infinity);
        default:
          return r.name;
      }
    };
    return [...rows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (typeof av === "string" || typeof bv === "string") {
        const cmp = String(av).localeCompare(String(bv));
        return cmp !== 0 ? cmp * dir : a.name.localeCompare(b.name);
      }
      if (av === bv) return a.name.localeCompare(b.name);
      return (av - bv) * dir;
    });
  }

  /**
   * The hold, as a bar rather than a fraction (16.97, Don's direction).
   *
   * "1,412 / 1,568 m3" is a number two other numbers have to be divided to
   * read; a bar is the answer already computed. It matters most at exactly the
   * moment a trader is deciding how much to buy, which is this screen.
   *
   * Both holds get a bar where the ship has both: a Digger's ore hold and its
   * general cargo fill independently (16.40), so one combined bar would be
   * wrong for the only role that most needs it. The fill colours the bar as it
   * approaches full, because "nearly out of room" is the state worth noticing
   * before a purchase clamps unexpectedly.
   */
  function holdBarsHtml(): string {
    const ship = state.currentShip;
    if (!ship) return "";
    const bar = (label: string, freeM3: number, totalM3: number): string => {
      if (totalM3 <= 0) return "";
      const { usedM3: used, pct, level } = holdFill(freeM3, totalM3);
      return `
        <div class="mk-hold" role="img"
             aria-label="${attr(label)} ${Math.round(used).toLocaleString()} of ${totalM3.toLocaleString()} cubic metres used">
          <span class="mk-hold-label">${escapeHtml(label)}</span>
          <span class="mk-hold-track">
            <span class="mk-hold-fill mk-hold-${level}" style="width:${pct.toFixed(1)}%"></span>
          </span>
          <span class="mk-hold-num">${Math.round(used).toLocaleString()} / ${totalM3.toLocaleString()} m³</span>
        </div>`;
    };
    return `<div class="mk-holds">
      ${bar("Cargo", freeCargoM3(ship.cargoM3, state.cargo, state.acceptedMissions), ship.cargoM3)}
      ${bar("Ore Hold", freeOreHoldM3(ship.oreHoldM3, state.cargo, state.acceptedMissions), ship.oreHoldM3)}
    </div>`;
  }

  function render(): void {
    wrap.innerHTML = "";
    renderHeader(wrap, ctx);

    const rows = sortRows(buildRows());
    const minutesLeft = Math.ceil(msUntilNextEpoch(Date.now()) / 60000);

    const intro = document.createElement("div");
    intro.innerHTML = hasStation
      ? `<p class="dim">
           Docked at ${system!.name} ${stationClass === "frontier" ? "Frontier Outpost" : "Hub Station"} &middot;
           ${state.credits.toLocaleString()} CR &middot; prices move in ${minutesLeft} min
           ${stationClass === "frontier" ? "&middot; thin stock, and it pays well for what it lacks" : ""}
         </p>
         ${holdBarsHtml()}`
      : `<p class="dim">No Station in this system &mdash; prices shown are indicative only. Dock at a Station to trade.</p>`;
    wrap.appendChild(intro);

    // The way out to the galaxy-wide view. Lives here rather than as another
    // icon in the Expedition View's row: a captain wants it while they are
    // already thinking about prices, and that row is long enough.
    const overview = document.createElement("button");
    overview.type = "button";
    overview.className = "mk-overview-link";
    overview.textContent = "Galaxy Prices — where to buy and sell across all stations →";
    overview.addEventListener("click", () => ctx.navigate("marketOverview"));
    wrap.appendChild(overview);

    if (state.lastEvent) {
      const log = document.createElement("p");
      log.className = "mk-log";
      log.textContent = state.lastEvent;
      wrap.appendChild(log);
    }

    const scroll = document.createElement("div");
    scroll.className = "mk-scroll";
    scroll.innerHTML = `
      <table class="mk-table">
        <thead>
          <tr>
            ${header("buy", "Buy")}
            ${header("stationQty", "Station")}
            ${header("cargoQty", "Ship")}
            ${header("sell", "Sell")}
            ${header("name", "Commodity")}
            ${header("type", "Type")}
            ${header("away", "Highest Demand")}
            ${header("haul", "Haul CR/m³")}
            ${header("hops", "Hops")}
          </tr>
        </thead>
        <tbody>
          ${rows.length === 0 ? `<tr><td colspan="9" class="dim">Nothing for sale here, and nothing in the hold.</td></tr>` : rows.map(rowHtml).join("")}
        </tbody>
      </table>
    `;
    wrap.appendChild(scroll);
    wireEvents(scroll);
  }

  function header(key: SortKey, label: string): string {
    const active = sortKey === key;
    const arrow = active ? (sortDesc ? " ▾" : " ▴") : "";
    return `<th><button type="button" class="mk-sort ${active ? "active" : ""}" data-sort="${key}">${label}${arrow}</button></th>`;
  }

  /**
   * The whole trade written out as arithmetic (16.96).
   *
   * The old tooltip named the destination but not the two prices, so a captain
   * comparing it against the columns on screen was comparing it against the
   * wrong pair -- both of the visible prices were local. Showing the subtraction
   * is what makes the green number checkable rather than trusted.
   */
  function haulExplanation(r: Row): string {
    if (r.bestSellPrice === null || r.haulTargetName === null || r.haulProfitPerM3 === null) return "";
    const from = r.haulIsBuyHere ? r.buyPrice : r.sellPrice;
    const perUnit = r.bestSellPrice - from;
    const lead = r.haulIsBuyHere
      ? `Buy here at ${from.toFixed(2)}`
      : `Sell here for only ${from.toFixed(2)}`;
    return (
      `${lead}, ${r.haulTargetName} pays ${r.bestSellPrice.toFixed(2)} — ` +
      `${perUnit >= 0 ? "+" : ""}${perUnit.toFixed(2)} per unit, ` +
      `${r.haulProfitPerM3.toFixed(2)} CR per m³ at ${r.volumePerUnit} m³/unit` +
      (r.haulIsBuyHere ? "" : " (you already own these)")
    );
  }

  /**
   * One row, in Don's 16.97 layout: the four controls a trade needs (Buy,
   * Station, Ship, Sell) in a single block on the left, then the reference half
   * (what it is, where it's wanted, what the run pays, how far).
   *
   * The prices moved INSIDE their buttons. Two columns went away and no numbers
   * did -- the thing you read and the thing you press are now the same object,
   * which is the whole argument for it on a tablet. A disabled button still
   * shows its price, because what the station pays is worth knowing before you
   * own any.
   */
  function rowHtml(r: Row): string {
    const canBuy =
      hasStation && r.stocked && r.buyAllowed && r.stationQty > 0 && unitsThatFit(r.name) > 0 && state.credits >= r.buyPrice;
    const canSell = hasStation && r.cargoQty > 0 && r.sellable;
    // Both fields pre-fill with the MAXIMUM, so pressing an untouched button is
    // buy-all / sell-all with no separate control (Don's 16.97 direction, and
    // 16.92's item 10 for the stock side). Typing overrides it; the value is
    // clamped rather than rejected, and the transaction itself takes the lesser
    // of what is typed, what fits and what is affordable.
    const buyField = buyQty.get(r.name) ?? r.stationQty;
    const sellField = sellQty.get(r.name) ?? r.cargoQty;
    const buyTitle = !r.buyAllowed
      ? "No ship in your fleet has a drone bay this size"
      : r.stocked
        ? `Buy ${buyField.toLocaleString()} at ${r.buyPrice.toFixed(2)} each`
        : "Not stocked here";
    const sellTitle = !r.sellable
      ? "Contraband can only be sold in pirate space"
      : r.cargoQty > 0
        ? `Sell ${sellField.toLocaleString()} at ${r.sellPrice.toFixed(2)} each`
        : "None aboard";
    const priced = (price: number | null, verb: string): string =>
      `<span class="mk-act-price">${price === null ? "—" : price.toFixed(2)}</span><span class="mk-act-verb">${verb}</span>`;
    return `
      <tr>
        <td><button type="button" class="mk-act mk-act-buy" data-buy="${attr(r.name)}"
                    title="${attr(buyTitle)}" ${canBuy ? "" : "disabled"}>
          ${priced(r.stocked ? r.buyPrice : null, "Buy")}
        </button></td>
        <td class="mk-qty">${
          r.stocked
            ? `<input type="text" inputmode="numeric" class="mk-input" data-qty-buy="${attr(r.name)}"
                      value="${buyField}" aria-label="Units of ${attr(r.name)} to buy" />`
            : `<span class="dim">—</span>`
        }</td>
        <td class="mk-qty">${
          r.cargoQty > 0
            ? `<input type="text" inputmode="numeric" class="mk-input" data-qty-sell="${attr(r.name)}"
                      value="${sellField}" aria-label="Units of ${attr(r.name)} to sell" />`
            : `<span class="dim">0</span>`
        }</td>
        <td><button type="button" class="mk-act mk-act-sell" data-sell="${attr(r.name)}"
                    title="${attr(sellTitle)}" ${canSell ? "" : "disabled"}>
          ${priced(r.sellable ? r.sellPrice : null, "Sell")}
        </button></td>
        <td>${escapeHtml(r.name)}</td>
        <td class="dim">${escapeHtml(String(r.category))}</td>
        <td class="mk-away">${
          r.bestSellPrice === null || r.haulTargetName === null
            ? `<span class="dim" title="Fixed price at every station">—</span>`
            : `<button type="button" class="mk-way ${state.waypointSystemId === r.haulTargetId ? "mk-way-set" : ""}"
                       data-waypoint="${attr(r.haulTargetId ?? "")}"
                       title="${attr(
                         state.waypointSystemId === r.haulTargetId
                           ? `Clear the waypoint on ${r.haulTargetName}`
                           : `Set course for ${r.haulTargetName}`
                       )}">
                 <span class="mk-way-price">${r.bestSellPrice.toFixed(2)}</span>
                 <span class="mk-way-where">${escapeHtml(r.haulTargetName)}${state.waypointSystemId === r.haulTargetId ? " ★" : ""}</span>
               </button>`
        }</td>
        <td class="mk-num ${r.haulProfitPerM3 !== null && r.haulProfitPerM3 > 0 ? "mk-good" : "dim"}"
            ${r.haulTargetName ? `title="${attr(haulExplanation(r))}"` : ""}>
          ${r.haulProfitPerM3 === null ? "—" : r.haulProfitPerM3.toFixed(2)}
        </td>
        <td class="mk-num mk-hops">${
          r.hops === null ? `<span class="dim">—</span>` : `${r.hops}`
        }</td>
      </tr>
    `;
  }

  function wireEvents(scope: HTMLElement): void {
    scope.querySelectorAll<HTMLButtonElement>("[data-sort]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.dataset.sort as SortKey;
        if (sortKey === key) sortDesc = !sortDesc;
        else {
          sortKey = key;
          // Text sorts read naturally ascending; numbers are almost always
          // wanted best-first. Hops is the exception among the numbers -- the
          // useful end of a distance is the NEAR end (16.97).
          sortDesc = key !== "name" && key !== "type" && key !== "hops";
        }
        render();
      });
    });

    const clampBuy = (name: string, raw: number): number => Math.max(0, Math.min(raw, stationQtyFor(name)));
    const clampSell = (name: string, raw: number): number => Math.max(0, Math.min(raw, state.cargo[name] ?? 0));

    scope.querySelectorAll<HTMLInputElement>("[data-qty-buy]").forEach((input) => {
      input.addEventListener("change", () => {
        const name = input.dataset.qtyBuy!;
        // Typed values above the remaining stock clamp down to it rather than
        // being rejected -- typing 999 is a legitimate way to say "all of it".
        buyQty.set(name, clampBuy(name, Math.floor(Number(input.value) || 0)));
        render();
      });
    });
    scope.querySelectorAll<HTMLInputElement>("[data-qty-sell]").forEach((input) => {
      input.addEventListener("change", () => {
        const name = input.dataset.qtySell!;
        sellQty.set(name, clampSell(name, Math.floor(Number(input.value) || 0)));
        render();
      });
    });

    // The Highest Demand station is a waypoint button (16.97), matching Galaxy
    // Prices: pressing the one already set clears it, because clicking your own
    // current course means "cancel that" far more often than "confirm it".
    scope.querySelectorAll<HTMLButtonElement>("[data-waypoint]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.waypoint!;
        if (!id) return;
        const name = otherStations.find((s) => s.id === id)?.name ?? id;
        if (state.waypointSystemId === id) {
          state.waypointSystemId = null;
          state.lastEvent = `Course to ${name} cleared.`;
        } else {
          state.waypointSystemId = id;
          state.lastEvent = `Course set for ${name}.`;
        }
        triggerSave(ctx);
        render();
      });
    });

    scope.querySelectorAll<HTMLButtonElement>("[data-buy]").forEach((btn) => {
      btn.addEventListener("click", () => buyItem(btn.dataset.buy!));
    });
    scope.querySelectorAll<HTMLButtonElement>("[data-sell]").forEach((btn) => {
      btn.addEventListener("click", () => sellItem(btn.dataset.sell!));
    });
  }

  function buyItem(name: string): void {
    if (!hasStation) return;
    // The drone size lock, enforced at the transaction and not only by a
    // disabled button (16.97). A UI-only guard is one refactor away from being
    // bypassed, and the failure it prevents costs 20,900 CR.
    if (!droneBuyAllowed(name, usableDrones)) return;
    const price = effectiveBuyPrice(name);
    const requested = buyQty.get(name) ?? stationQtyFor(name);
    // Three independent ceilings, and the smallest wins: what the station has,
    // what the hold takes, what the credits cover. Defaulting to the full stack
    // and silently taking less is the behaviour Don specified -- the remaining
    // stock and falling credits are the feedback.
    const affordable = price > 0 ? Math.floor(state.credits / price) : 0;
    const units = Math.min(requested, stationQtyFor(name), unitsThatFit(name), affordable);
    if (units <= 0) return;

    const cost = Math.round(units * price);
    state.credits -= cost;
    state.cargo[name] = (state.cargo[name] ?? 0) + units;
    state.stationPurchases[purchaseKey(name)] = (state.stationPurchases[purchaseKey(name)] ?? 0) + units;
    buyQty.delete(name);
    creditTradeStanding(cost);
    state.lastEvent = `Bought ${units.toLocaleString()} ${name} for ${cost.toLocaleString()} CR.`;
    triggerSave(ctx);
    render();
  }

  function sellItem(name: string): void {
    if (!hasStation) return;
    if (!canSellHere(name, region)) return;
    const held = state.cargo[name] ?? 0;
    const units = Math.min(sellQty.get(name) ?? held, held);
    if (units <= 0) return;

    const earned = Math.round(units * stationSellPrice(stationId, region, name, epoch, units));
    const remaining = held - units;
    if (remaining > 0) state.cargo[name] = remaining;
    else delete state.cargo[name];
    state.credits += earned;
    // Selling back returns stock to the station, so a captain cannot drain a
    // station and then repeatedly dump into it for a stock-free profit.
    const key = purchaseKey(name);
    const taken = state.stationPurchases[key] ?? 0;
    if (taken > 0) state.stationPurchases[key] = Math.max(0, taken - units);
    sellQty.delete(name);
    creditTradeStanding(earned);
    state.lastEvent = `Sold ${units.toLocaleString()} ${name} for ${earned.toLocaleString()} CR.`;
    triggerSave(ctx);
    render();
  }

  render();
  container.appendChild(wrap);
}

function attr(value: string): string {
  return value.replace(/"/g, "&quot;");
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
